import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select, SelectOption } from "common/components/Select";
import { contentStatusFilterValueLabel } from "common/content-status-filter";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { CreateContentFooterBox } from "domain/admin/common/CreateContentFooterBox";
import { listFilter } from "domain/admin/util";
import { ContentStatusFilterValue } from "domain/constants";
import gql from "graphql-tag";
import * as React from "react";
import { GetStudyProgrammesAdministration } from "__generated__/types";
import { StudyProgrammeCreate } from "../studyprogramme/StudyProgrammeCreate";

const AdministrationList = React.lazy(() =>
  import("domain/admin/common/AdministrationList").then((it) => ({
    default: it.AdministrationList,
  })),
);

const DATA_QUERY = gql`
  query GetStudyProgrammesAdministration {
    studyProgrammeList {
      id
      status
      title
    }
  }
`;

export const AdminPage: React.FC = () => {
  const [contentStatusFilterValue, setContentStatusFilterValue] =
    React.useState<ContentStatusFilterValue>(ContentStatusFilterValue.ALL);
  const { data, loadingOrError, refetch } =
    useSimpleQuery<GetStudyProgrammesAdministration>(DATA_QUERY);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.studyProgrammeList) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const filteredList = data.studyProgrammeList.filter((v) =>
    listFilter(v, contentStatusFilterValue),
  );

  const contentStatusFilterValueToSelectOption = (
    contentStatusFilterValue: ContentStatusFilterValue,
  ): SelectOption => {
    return {
      key: contentStatusFilterValue.toString(),
      value: contentStatusFilterValue.toString(),
      label: contentStatusFilterValueLabel(contentStatusFilterValue),
    };
  };

  return (
    <AdministrationLayout title="Studieprogrammer" context={[]}>
      <Select
        name="status"
        title="Status"
        options={Object.values(ContentStatusFilterValue).map(
          contentStatusFilterValueToSelectOption,
        )}
        value={contentStatusFilterValue.toString()}
        onChange={(newValue: string) => {
          setContentStatusFilterValue(newValue as ContentStatusFilterValue);
        }}
      />

      {data.studyProgrammeList.length === 0 ? (
        <p>Ingen studieprogrammer eksisterer.</p>
      ) : (
        filteredList.length === 0 && (
          <p>
            Ingen studieprogrammer eksisterer for status &quot;
            {contentStatusFilterValueLabel(contentStatusFilterValue)}&quot;
          </p>
        )
      )}
      {filteredList.length > 0 && (
        <AdministrationList
          showElementIcons
          isInSyllabus={false}
          elements={filteredList.map((it) => ({
            category: "StudyProgramme",
            type: "StudyProgramme",
            id: it.id,
            status: it.status,
            title: it.title,
            contentId: it.id,
          }))}
          complete={refetch}
          canDelete={false}
        />
      )}
      <CreateContentFooterBox
        buttonText="Opprett studieprogram"
        content={StudyProgrammeCreate}
        onUpdated={() => {
          void refetch();
        }}
      />
    </AdministrationLayout>
  );
};
